import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, type Unsubscribe } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { sortDayPlanEvents } from "@/features/day-plan/sortDayPlanEvents";
import { useWedding } from "./useWedding";
import type { DayPlanEvent, DayPlanPerson } from "@amazing-wedding/shared-types";

interface UseDayPlanResult {
  events: DayPlanEvent[];
  /** Calendar columns, ordered. Comes free with the wedding doc. */
  people: DayPlanPerson[];
  loading: boolean;
}

/**
 * Subscribes to /weddings/{wid}/dayPlanEvents and exposes the embedded
 * `dayPlanPeople` columns from the wedding doc (no extra subscription).
 * Events sort by `order` ASC with a stable hour:minute tiebreaker. Lists stay
 * tiny (~10–60 docs) so we pull everything into memory — same pattern as
 * useTasks / useGuests.
 */
export function useDayPlan(): UseDayPlanResult {
  const { wedding, loading: weddingLoading } = useWedding();
  const [events, setEvents] = useState<DayPlanEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (weddingLoading) {
      setLoading(true);
      return;
    }
    if (!wedding) {
      setEvents([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub: Unsubscribe = onSnapshot(
      collection(db, "weddings", wedding.id, "dayPlanEvents"),
      (snap) => {
        const rows: DayPlanEvent[] = [];
        snap.forEach((d) => {
          const data = d.data() as Omit<DayPlanEvent, "id">;
          rows.push({ id: d.id, ...data });
        });
        setEvents(sortDayPlanEvents(rows));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [wedding, weddingLoading]);

  // Stable identity across renders so the calendar (which keeps refs keyed by
  // person id and re-renders on every pointermove) doesn't see a new array.
  const people = useMemo(
    () => [...(wedding?.dayPlanPeople ?? [])].sort((a, b) => a.order - b.order),
    [wedding?.dayPlanPeople],
  );

  return { events, people, loading };
}
